import type { UseEmblaCarouselType } from "embla-carousel-react";
import { useEffect, useRef, useState } from "react";

import CentraImage from "@/components/CentraImage";
import type { getPackShotGalleryComponent } from "@/lib/api/formatting/rorosTweed/productPage";
import useMediaGallery from "@/lib/hooks/useMediaGallery";
import ZoomSvg from "@/public/svg/Zoom.svg";

import styles from "./packshotGallery.module.scss";

type PackShotGalleryUI = Partial<
  ReturnType<typeof getPackShotGalleryComponent>
> & {
  increment: () => void;
  setZoomed: (value: boolean) => void;
  zoomed: boolean;
  decrement: () => void;
  imageIndex: number;
  imageSetters: () => {
    active: boolean;
    callback: () => void;
    value: number;
  }[];
  mobile: boolean;
  emblaRef: UseEmblaCarouselType[0];
};

const UI = (props: PackShotGalleryUI) => {
  const {
    media,
    increment,
    decrement,
    imageIndex,
    imageSetters,
    mobile,
    emblaRef,
    setZoomed,
    zoomed,
  } = props;
  const { findMedia } = useMediaGallery("Product");
  const imgRef = useRef<HTMLDivElement>(null);
  const [x, setX] = useState(0);
  const [y, setY] = useState(0);

  useEffect(() => {
    if (zoomed) {
      const img = imgRef.current;
      if (!img) return;
      imgRef?.current?.addEventListener("mousemove", (e) => {
        const { left, top, width, height } = img?.getBoundingClientRect();
        const x = ((e.clientX - left) / width) * 100;
        const y = ((e.clientY - top) / height) * 100;

        setX(x);
        setY(y);
      });
    }
    return () => {
      const img = imgRef.current;
      if (!img) return;
      imgRef.current.removeEventListener("mousemove", (e) => {
        const { left, top, width, height } = img.getBoundingClientRect();
        const x = ((e.clientX - left) / width) * 100;
        const y = ((e.clientY - top) / height) * 100;

        setX(x);
        setY(y);
      });
    };
  }, [zoomed]);

  if (!media || !media[0]) {
    return <></>;
  }

  const image = media[imageIndex];

  const setters = imageSetters();

  const zoom = () => {
    setZoomed(!zoomed);
  };

  if (!mobile) {
    return (
      <figure className={styles.container} ref={imgRef}>
        <button className={styles.zoom} onClick={zoom}>
          <ZoomSvg />
        </button>
        <div className={styles.wrapper}>
          {!zoomed ? (
            <div className={styles["buttons-wrapper"]}>
              <button onClick={decrement} />

              <button onClick={increment} />
            </div>
          ) : (
            <div
              className={styles["zoom-wrapper"]}
              onMouseLeave={() => setZoomed(false)}
              onClick={zoom}
            ></div>
          )}

          <CentraImage
            style={
              zoomed
                ? {
                    transformOrigin: `left top`,

                    transform: `translate(-${x}%, -${y}%) scale(${
                      zoomed ? 2 : 1
                    })`,
                  }
                : {}
            }
            src={image?.uri}
            alt={image?.alt}
          />
        </div>
        {!mobile && (
          <div className={styles.selectorWrapper}>
            <div className={styles.selector}>
              {media.length > 1 && (
                <button onClick={decrement} className={styles.decrement} />
              )}
              <div className={styles.setters}>
                {setters.map((setter) => (
                  <button
                    className={`${styles.setter} ${
                      setter.active ? styles.active : ""
                    }`}
                    key={`packshot-image-setter-${setter.value}`}
                    onClick={setter.callback}
                  >
                    {setter.value}
                  </button>
                ))}
              </div>
              {media.length > 1 && (
                <button onClick={increment} className={styles.increment} />
              )}
            </div>
          </div>
        )}
      </figure>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.gridWrapper} ref={emblaRef}>
        <div className={styles.imageGridMobile}>
          {media.map((image, i) => {
            return (
              <div
                key={`product-media-grid-${i}`}
                className={styles.imageWrapper}
              >
                <div
                  className={styles.imageContainer}
                  key={i}
                  onClick={() => findMedia(image)}
                >
                  <CentraImage
                    src={image.uri}
                    layout="fill"
                    objectFit="cover"
                    alt={image.alt}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default UI;
